import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';

import MainLayout from "../layouts/MainLayout";
import PageTitle from '../components/UtilityHelpers/PageTitle';
import ApiService from '../services/ApiServiceClass';

import { useAuth } from "../hooks/useAuth";
import { useLoader } from "../hooks/useLoader";
import { useCategoryTypeForRadio } from "../hooks/OtherTables/useCategoryTypeForRadio";
import { useCategoryForRadio, TransformedCategoryType } from "../hooks/OtherTables/useCategoryForRadio";

import { Modal, Alert, Button } from "flowbite-react";

import CustomThemeModal from '../components/CustomThemes/CustomThemeModal';

import FormMembersField from "../components/Forms/FormMembersField";
import RadioField from "../components/Forms/RadioField";
import SelectField from "../components/Forms/SelectField";
import TextAreaField from "../components/Forms/TextareaField";
import InputField from "../components/Forms/InputField";

import { SubmitHandler, useForm } from 'react-hook-form';
import { IOptions } from '../types/IOptions';

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { useGetRequestByID } from "../hooks/Request/useGetRequestByID";
import ReadOnlyInputField from "../components/Forms/ReadOnlyInputField";
import Comments from "../components/Comments/Comments";

import { inforMessageArray } from "../data/infoMessageArray";
import AlertBanner from "../components/UtilityHelpers/AlertBanner";
import StatusLabel from "../components/UtilityHelpers/StatusLabel";
import RequestItemField from "../components/Forms/RequestItemField/RequestItemField";

const RequestSchema = z.object({
    names: z.string(),
    requestType: z.string(),
    isHealthSafetyRisk: z.string(),
    healthSafetyDetails: z.string(),
    requestCategory: z.string({ invalid_type_error: "Please select a request category." }),
    requestTitle: z.string({
        required_error: "Title should not be empty.",
    }).min(1, { message: "Title should not be empty." }),
    requestDescription: z.string({
        required_error: "Description should not be empty.",
    }).min(1, { message: "Description should not be empty." }),
    itemCount: z.number().min(1, { message: "You need to add at least one item." }),
    totalCost: z.number(),
});

type FormFields = z.infer<typeof RequestSchema>;

const NewRequest = () => {
    const [currentCategoryOptions, setCurrentCategoryOptions] = useState<TransformedCategoryType[]>([]);
    const [searchParams] = useSearchParams();
    const [isCategoryDataLoaded, setIsCategoryDataLoaded] = useState(false);
    const [currentSelectedCategory, setCurrentSelectedCategory] = useState("18");
    const [requestStatus, setRequestStatus] = useState("");    

    const [openModal, setOpenModal] = useState(false);
    const [modalMessage, setModalMessage] = useState<string>('Draft successfully saved!');
    const [submitAction, setSubmitAction] = useState("DRAFT");

    const requestIDFromParam = searchParams.get("id");
    const request_id_from_param: number = requestIDFromParam !== null && requestIDFromParam !== undefined ? parseInt(requestIDFromParam) : 0;

    const loader = useLoader();
    const auth = useAuth();
    const navigate = useNavigate();


    const { fetchRequestsByID } = useGetRequestByID(request_id_from_param);
    const { categoriesForRadio, setCategoriesForRadio, fetchCategories } = useCategoryForRadio();
    const { categoryTypesForRadio, setCategoryTypesForRadio, fetchCategoryTypes } = useCategoryTypeForRadio();
    const isHealthSafetyRiskOptions = [{"id": "YES", "value": "YES"}, {"id": "NO", "value": "NO"}];

    const { register, handleSubmit, watch, getValues, setValue, clearErrors, unregister, control, formState: { errors, isSubmitting } } = useForm<FormFields>({
        defaultValues: {
            names: auth?.firstName + " " + auth?.lastName
        },
        resolver: zodResolver(RequestSchema)
    });

    let numCount = 1;

    const requestType = watch("requestType");
    const requestCategory = watch("requestCategory");
    const isHealthSafetyRisk = watch("isHealthSafetyRisk");

    const actionAfterCloseModal = () => {        
        setOpenModal(false);
        navigate("/request");
    }    

    const actionAfterSubmit = () => {
        setModalMessage("Request updated and submitted!")
        setOpenModal(true);
    }    

    const onSubmit: SubmitHandler<FormFields> = () => {
        setSubmitAction("SUBMIT");
        const formData = new FormData();

        formData.append('requestID', request_id_from_param.toString());
        formData.append('requestType', getValues("requestType"));
        formData.append('isHealthSafetyRisk', getValues("isHealthSafetyRisk"));
        formData.append('healthSafetyDetails', getValues("healthSafetyDetails"));
        formData.append('requestCategory', getValues("requestCategory"));
        formData.append('requestTitle', getValues("requestTitle"));
        formData.append('requestDescription', getValues("requestDescription"));
        formData.append('totalCost', getValues("totalCost").toString());

        try {
            loader?.setIsLoading(true);
            ApiService.submitRequest(formData)
                .then(() => {
                    loader?.setIsLoading(false);                   
                    actionAfterSubmit();
                })
                .catch((error) => {
                    loader?.setIsLoading(false);
                    console.log(error);
                });
        } catch(error) {
            loader?.setIsLoading(false);
            console.error(error);
        }
    }

    const saveDraft = () => {
        setSubmitAction("DRAFT");

        const formData = new FormData();

        formData.append('requestID', request_id_from_param.toString());
        formData.append('requestType', getValues("requestType"));
        formData.append('isHealthSafetyRisk', getValues("isHealthSafetyRisk"));
        formData.append('healthSafetyDetails', getValues("healthSafetyDetails"));        
        formData.append('requestCategory', getValues("requestCategory"));
        formData.append('requestTitle', getValues("requestTitle"));
        formData.append('requestDescription', getValues("requestDescription"));
        formData.append('totalCost', getValues("totalCost").toString());

        try {
            loader?.setIsLoading(true);
            ApiService.saveDraft(formData)
                .then(() => {
                    loader?.setIsLoading(false);
                    setOpenModal(true);
                })
                .catch((error) => {
                    loader?.setIsLoading(false);
                    console.log(error);
                });
        } catch(error) {
            loader?.setIsLoading(false);
            console.error(error);
        }
    }

    const onInvalid = (errors: any) => {
        console.log(errors);
    }


    useEffect(() => {

        const loadData = async () => {
            try {
                loader?.setIsLoading(true);

                const requestData = await fetchRequestsByID();
                await fetchCategories();
                await fetchCategoryTypes();

                setValue("isHealthSafetyRisk", requestData[0].is_health_safety_risk)
                setValue("healthSafetyDetails", requestData[0].health_safety_details)
                setValue("requestType", requestData[0].category_type_id.toString());
                setValue("requestDescription", requestData[0].description);
                setValue("requestTitle", requestData[0].title);                
                setCurrentSelectedCategory(requestData[0].category_id.toString());                
                setRequestStatus(requestData[0].status);
                loader?.setIsLoading(false);
            } catch (error) {
                loader?.setIsLoading(false);
                console.error(error);
            }
        }

        loadData();

    }, []);

    useEffect(() => {
        if (requestType) {
            setCurrentCategoryOptions(categoriesForRadio.filter((item => item.category_type_id == requestType)));
            setIsCategoryDataLoaded(true);
        }
    }, [requestType]);

    useEffect(() => {
        if (isCategoryDataLoaded) {
            setValue("requestCategory", currentSelectedCategory);
        }
    }, [isCategoryDataLoaded]);

    useEffect(() => {
        if (["Approved", "Declined", "Ordered", "Purchased", "Delivered"].includes(requestStatus)) {
            navigate("/");
        }
    }, [requestStatus]);

    return (
        <>
            <PageTitle title="OPS | Request Details" />
            <MainLayout>
                <form onSubmit={handleSubmit(onSubmit, onInvalid)} >
                    <div className="col-span-12 rounded-sm border border-stroke bg-white px-5 pt-7.5 pb-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 ">
                        <h5 className="text-xl font-semibold text-black dark:text-white mb-5">
                            Request Details
                        </h5>
                        <div className="relative z-0 w-full mb-5 group">
                            <div className="flex flex-row">
                                <div className="mr-2">
                                    Status : 
                                </div>
                                <div>
                                    <StatusLabel statusLabel={requestStatus} />        
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-between items-center">
                            <div></div>
                            { requestStatus != "Draft" && <Comments requestID={request_id_from_param}/>}                        
                        </div> 
                        <div>
                            <FormMembersField
                                itemNumber={numCount}
                                requestID={requestIDFromParam}
                                labelName='List of Names.'
                                fieldName="names"
                                setItemizedFieldValue={setValue}
                                getItemizedFieldValue={getValues}
                                errorsItemizedField={errors}
                                infoTagMessage={inforMessageArray[0]}
                                type="hidden"
                                registerItemizedField={register("names")}
                            />
                        </div>
                        <div>
                            <RadioField
                                itemNumber={numCount = numCount + 1}
                                labelName='Request Type'
                                fieldName="requestType"
                                options={categoryTypesForRadio}
                                infoTagMessage={inforMessageArray[1]}
                                errors={errors}
                                register={register('requestType')}
                            />
                        </div>
                        <div>
                            <SelectField
                                itemNumber={numCount = numCount + 1}
                                labelName='Request Category'
                                fieldName="requestCategory"
                                options={currentCategoryOptions}
                                infoTagMessage={inforMessageArray[2]}
                                errors={errors}
                                register={register('requestCategory', { shouldUnregister: true })}
                            />
                        </div>
                        <div>
                            <InputField 
                                itemNumber={numCount = numCount + 1}
                                labelName={<>Title</>}
                                fieldName="requestTitle"
                                infoTagMessage={inforMessageArray[3]}
                                errors={errors}
                                placeholder="Enter title"
                                register={register("requestTitle")}                              
                            />
                        </div>
                        <div>
                            <TextAreaField
                                itemNumber={numCount = numCount + 1}
                                labelName={<>Benefit or Business Case</>}
                                fieldName="requestDescription"
                                infoTagMessage={inforMessageArray[4]}
                                errors={errors}
                                placeholder="Enter description"
                                register={register("requestDescription")}
                            />
                        </div>
                        <div>
                            <RequestItemField
                                itemNumber={numCount = numCount + 1}
                                requestID={requestIDFromParam}
                                labelName='Itemized list of parts with specifications or model numbers requested. Maximum OPEX cost for each IT part is NZ$1000 ex. GST or NZ$5000 ex. GST for each NON-IT part.'
                                fieldName="itemCount"
                                setItemizedFieldValue={setValue}
                                errorsItemizedField={errors}
                                clearItemizedFieldErorrs={clearErrors}
                                infoTagMessage={inforMessageArray[5]}
                                type="hidden"
                                placeholder="Enter short title..."
                                registerItemizedField={register("itemCount")}
                            />
                        </div>
                        <div>
                            <RadioField
                                itemNumber={numCount = numCount + 1}
                                labelName='Are the items requested in any way restricted by law or (University) rules and regulations? Do they pose any health, safety or wellbeing (HSW) risks? If yes, please explain in detail how these issues will be addressed and by whom?'
                                fieldName="isHealthSafetyRisk"
                                options={isHealthSafetyRiskOptions}
                                infoTagMessage={inforMessageArray[6]}
                                errors={errors}
                                register={register('isHealthSafetyRisk')}
                            />
                        </div>
                        <div>
                            {isHealthSafetyRisk == "YES" &&
                                <TextAreaField
                                    itemNumber={numCount = numCount + 1}
                                    labelName={<>Health & Safety Details</>}
                                    fieldName="healthSafetyDetails"
                                    infoTagMessage={inforMessageArray[7]}
                                    errors={errors}
                                    placeholder="Enter details"
                                    register={register("healthSafetyDetails")}
                                />
                            }
                        </div>
                        <div>
                            <ReadOnlyInputField
                                itemNumber={numCount = numCount + 1}
                                labelName='Total cost in NZD calculated from the above items'
                                fieldName="totalCost"
                                step="any"
                                errors={errors}
                                disabled
                                type="number"
                                watchItemizedFieldValue={watch}
                                getItemizedFieldValue={getValues}
                                infoTagMessage={inforMessageArray[8]}
                                placeholder="Enter total cost here..."
                                register={register('totalCost', { valueAsNumber: true })}                                
                            />
                        </div>

                        <div className="mt-10 flex justify-end">
                            {
                                requestStatus == "Draft" && <>
                                    <button type="submit"
                                        className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center mb-10 mr-2"
                                    >
                                        Submit
                                    </button>

                                    <button type="button"
                                        className="text-blue-700 hover:text-white border border-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:border-blue-500 dark:text-blue-500 dark:hover:text-white dark:hover:bg-blue-500 dark:focus:ring-blue-800 mb-10"
                                        onClick={() => saveDraft()}
                                    >
                                        Save as Draft
                                    </button>    
                                </>
                            }

                            {
                                requestStatus != "Draft" && <>
                                    <button type="submit"
                                        className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center mb-10 mr-2"
                                    >
                                        Update Request
                                    </button>
                                </>
                            }
                            
                        </div>
                    </div>
                </form>

                <Modal show={openModal} onClose={() => setOpenModal(false)} size={'sm'}  theme={CustomThemeModal}>
                    <Modal.Body>
                        <div className="text-center">
                            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                                {modalMessage}
                            </h3>                            
                            <div className="flex justify-center gap-4">
                                {submitAction == "DRAFT" && (
                                    <Button onClick={() => setOpenModal(false)}>Closed</Button>
                                )}
                                {submitAction == "SUBMIT" && (
                                    <Button onClick={() => actionAfterCloseModal()}>Closed</Button>
                                )}    
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>

            </MainLayout>
        </>
    )
}

export default NewRequest;
